import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Reorder } from 'framer-motion';
import { DraggableTab } from './DraggableTab';
import { HydratedPost, UserData, PostType, Post } from '../types';
import { useAuth, useAudioPlayerState } from '../contexts/AuthContext';
import { ProfileHeader } from './ProfileHeader';
import { PostGrid } from './PostGrid';
import { stickerTextStyle } from './StickerText';
import ProxiedIframe from './ProxiedIframe';
import { PostOptionsModal } from './Modals';

interface ProfilePageProps {
    posts: HydratedPost[];
    allPosts: HydratedPost[];
    onPostClick: (post: HydratedPost, contextList?: HydratedPost[]) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number) => void;
    onNavigateToProfile: (userId: string) => void;
    onNavigateToHashtag: (tag: string) => void;
    onNavigateToCommunity?: (communityId: string, channelId?: string) => void;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    tabsOrder?: string[];
    onReorderTabs?: (newOrder: string[]) => void;
    onCreatePost: (postData: {
        type: PostType,
        content: Post['content'],
        visibility?: 'public' | 'followers' | 'groups' | 'private',
        visibilityGroups?: string[],
        showCaption?: boolean,
        showOnProfile?: boolean,
        textSize?: 'xs' | 'sm' | 'med' | 'lg',
        communityId?: string,
        themeOpacity?: number,
        themeColor?: string,
        textColor?: string,
        isOptimistic?: boolean,
        localId?: number,
        fontId?: string,
        postcardStyle?: string,
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall'
    }) => void;
    isOwner: boolean;
    viewedUser: { id: string, data: UserData };
    onProfilePictureChange: (url: string) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    onProfileBgChange: (bg: { url: string, style: string }) => void;
    galleryAnimation: string;
    postBlurOpacity: number;
    onStartConversation: (userId: string) => void;
    onFollowToggle: (userId: string) => void;
    onShowFollowList: (listType: 'followers' | 'following' | 'friends' | 'connections', userId: string) => void;
    onVouch?: (postId: number) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onRemoveFollower?: (userId: string) => void;
    onReportPost?: (postId: number) => void;
    gridColumns?: 1 | 2 | 3 | 4 | 5 | 6;
    onGridColumnsChange?: (cols: 1 | 2 | 3 | 4 | 5 | 6) => void;
    preferredScale?: number;
    onSwipeTransition?: (direction: 'left' | 'right') => void;
    onScaleChange?: (scale: number) => void;
    isModalOpen?: boolean;
    isDemo?: boolean;
    currentUser: string | null;
    onVisibleCountChange?: (count: number) => void;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    initialVisibleCount?: number;
    isLoading?: boolean;
    onAddToCart?: (post: Post) => void;
    initialSharedContent?: { text: string, media: any[], sourcePostId?: number } | null;
    clearSharedContent?: () => void;
    onNavigateToYipYap?: (config: { name: string, mode: 'broadcast' | 'anonymous' | 'forum', visibility: 'public' | 'open', groupIds?: string[] }) => void;
    existingNonAnonymousYipYapNames?: string[];
    hideContent?: boolean;
    onPin?: (postId: number) => void;
    onUnpin?: (postId: number) => void;
    onReorderPinnedPosts?: (newPinnedOrder: number[]) => void;
    onRetryPostUpload?: (localId: number, startUpload: any) => void;
    postcardGap?: number;
    onPostDragProgress?: (post: any, progress: number, direction: 'left' | 'right' | null) => void;
    onMassEdit?: (postIds: number[], updates: Partial<Post>) => void;
    onMassArchive?: (postIds: number[]) => void;
    onMassDelete?: (postIds: number[]) => void;
}

export const ProfilePage = ({
    posts,
    allPosts,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToProfile,
    onNavigateToHashtag,
    onNavigateToCommunity,
    onShare,
    tabsOrder,
    onReorderTabs,
    onCreatePost,
    isOwner,
    viewedUser,
    onProfilePictureChange,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    onProfileBgChange,
    galleryAnimation,
    postBlurOpacity,
    onStartConversation,
    onFollowToggle,
    onShowFollowList,
    onVouch,
    onGrantAdmin,
    onDeleteUser,
    onMuteUser,
    onBlockUser,
    onRemoveFollower,
    onReportPost,
    gridColumns = 1,
    onGridColumnsChange,
    preferredScale = 1,
    onScaleChange,
    onSwipeTransition,
    isModalOpen = false,
    currentUser,
    initialVisibleCount = 24,
    onVisibleCountChange,
    onInternalModalStateChange,
    closeInternalModalsTrigger = 0,
    isLoading,
    initialSharedContent,
    clearSharedContent,
    onNavigateToYipYap,
    hideContent = false,
    existingNonAnonymousYipYapNames,
    onPin,
    onUnpin,
    onReorderPinnedPosts,
    onRetryPostUpload,
    onAddToCart,
    postcardGap = 8,
    onPostDragProgress,
    onMassEdit,
    onMassArchive,
}: ProfilePageProps) => {
    const auth = useAuth() || {};
    const { currentUser: authUser, usersData: authUsersData } = auth;
    const audioPlayerState = useAudioPlayerState() || {};
    const { playAudioPost } = audioPlayerState;
    const currentUserData = authUsersData?.[currentUser || authUser || ''];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const hexToRgb = (hex: string) => {
        let c = hex.replace('#', '');
        if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
        const r = parseInt(c.substring(0, 2), 16), g = parseInt(c.substring(2, 4), 16), b = parseInt(c.substring(4, 6), 16);
        return isNaN(r) || isNaN(g) || isNaN(b) ? null : { r, g, b };
    };

    const getExactModalBg = (color: string, opacityVal: number) => {
        const rgb = hexToRgb(color);
        if (!rgb) return `rgba(0, 0, 0, ${opacityVal})`;
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const [activeTabState, setActiveTabState] = useState<string | null>(null);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [headerModalOpen, setHeaderModalOpen] = useState(false);
    const [showPassionOverlay, setShowPassionOverlay] = useState(false);

    // Mass Edit State
    const [massEditMode, setMassEditMode] = useState<'edit' | 'archive' | 'delete' | null>(null);
    const [selectedPostIds, setSelectedPostIds] = useState<Set<number>>(new Set());
    const [isMassEditCustomizeModalOpen, setIsMassEditCustomizeModalOpen] = useState(false);
    const [showMassConfirmModal, setShowMassConfirmModal] = useState(false);
    const [pendingMassUpdateData, setPendingMassUpdateData] = useState<Partial<Post> | null>(null);

    useEffect(() => {
        const handleStartMassEditMode = (e: Event) => {
            if (!isOwner) return;
            const action = (e as CustomEvent).detail?.action;
            if (action === 'edit' || action === 'archive' || action === 'delete') {
                setMassEditMode(action);
                setSelectedPostIds(new Set());
            }
        };
        window.addEventListener('app.startMassEditMode', handleStartMassEditMode);
        return () => window.removeEventListener('app.startMassEditMode', handleStartMassEditMode);
    }, [isOwner]);

    const toggleSelectPost = (postId: number) => {
        setSelectedPostIds(prev => {
            const next = new Set(prev);
            if (next.has(postId)) {
                next.delete(postId);
            } else {
                next.add(postId);
            }
            return next;
        });
    };

    const handleActionDone = () => {
        if (selectedPostIds.size === 0) return;
        if (massEditMode === 'edit') {
            setIsMassEditCustomizeModalOpen(true);
        } else {
            setShowMassConfirmModal(true);
        }
    };

    const autoplayedUserRef = useRef<string | null>(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [viewedUser.id]);

    useEffect(() => {
        if (viewedUser.id && autoplayedUserRef.current !== viewedUser.id) {
            if (viewedUser.data?.playlist && viewedUser.data.playlist.length > 0 && playAudioPost) {
                autoplayedUserRef.current = viewedUser.id;
                const tracks = viewedUser.data.playlist.map((track: any) => ({
                    id: track.postId || `playlist-${viewedUser.id}-${track.id}`,
                    author: viewedUser.id,
                    type: 'audio',
                    content: {
                        text: track.title,
                        media: [{ url: track.url, type: 'audio' }]
                    },
                    timestamp: new Date().toISOString()
                }));
                const firstTrack = tracks[0];
                playAudioPost(firstTrack, tracks);
            }
        }
    }, [viewedUser.id, viewedUser.data?.playlist, playAudioPost]);

    const isFollowing = useMemo(() => {
        if (!currentUser || !viewedUser.id) return false;
        return viewedUser.data.followers?.includes(currentUser) || false;
    }, [currentUser, viewedUser.id, viewedUser.data.followers]);

    const canSeeComments = useMemo(() => {
        if (isOwner) return true;
        const visibility = viewedUser.data.commentsTabVisibility || 'public';
        if (visibility === 'public') return true;
        if (visibility === 'followers') return isFollowing;
        return false;
    }, [isOwner, viewedUser.data.commentsTabVisibility, currentUser, isFollowing]);

    const profileTabs = useMemo(() => {
        let baseTabs = isOwner
            ? ['All posts', 'OC', 'Bumps', 'Footprints', 'Media', 'Audio', 'Links', 'Yaps', 'Faves', 'Saves', 'Archive']
            : ['All posts', 'OC', 'Bumps', 'Footprints', 'Media', 'Audio', 'Links', 'Yaps'];

        if (viewedUser.data?.isVendor) {
            baseTabs = ['For Sale', ...baseTabs];
        }



        if (!tabsOrder || tabsOrder.length === 0) return baseTabs;

        const orderedTabs: string[] = [];
        const baseTabsSet = new Set(baseTabs);

        tabsOrder.forEach(id => {
            if (baseTabsSet.has(id)) {
                orderedTabs.push(id);
                baseTabsSet.delete(id);
            }
        });

        baseTabsSet.forEach(t => orderedTabs.push(t));
        return orderedTabs;
    }, [isOwner, canSeeComments, tabsOrder]);

    const activeTab = activeTabState || profileTabs[0] || 'All posts';
    const setActiveTab = setActiveTabState;

    useEffect(() => {
        onInternalModalStateChange?.(headerModalOpen);
    }, [headerModalOpen, onInternalModalStateChange]);

    const userPosts = useMemo(() => {
        return posts.filter(p => p.author === viewedUser.id);
    }, [posts, viewedUser.id]);

    const filteredPosts = useMemo(() => {
        if (activeTab === 'Archive') {
            return isOwner ? userPosts.filter(p => p.isArchived) : [];
        }

        const pinSortedUserPosts = (() => {
            const rawUser = userPosts.filter(p => !p.isArchived && (isOwner || p.showOnProfile !== false));
            const pinnedIds = viewedUser.data.pinnedPosts || [];

            const pinned = rawUser.filter(p => pinnedIds.includes(p.id));
            pinned.sort((a, b) => pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id));

            const unpinned = rawUser.filter(p => !pinnedIds.includes(p.id));
            return [...pinned, ...unpinned];
        })();

        const unarchivedAllPosts = allPosts.filter(p => !p.isArchived);

        switch (activeTab) {
            case 'For Sale':
                return pinSortedUserPosts.filter(p => p.isForSale);
            case 'OC':
                return pinSortedUserPosts.filter(p => p.type !== 'bump');
            case 'Bumps':
                return pinSortedUserPosts.filter(p => p.type === 'bump');
            case 'Footprints':
                return pinSortedUserPosts.filter(p => !!p.footprintDetails);
            case 'Media':
                return pinSortedUserPosts.filter(p => p.type === 'image' || p.type === 'video');
            case 'Audio':
                return pinSortedUserPosts.filter(p => p.type === 'audio' || p.type === 'voice');
            case 'Links':
                return pinSortedUserPosts.filter(p => p.type === 'link');
            case 'Yaps': {
                const ownTextPosts = pinSortedUserPosts.filter(p => p.type === 'text');
                const commentedPosts = canSeeComments
                    ? unarchivedAllPosts.filter(p => p.comments.some(c => c.author === viewedUser.id))
                    : [];
                
                const combined = [...ownTextPosts];
                commentedPosts.forEach(cp => {
                    if (!combined.some(p => p.id === cp.id)) {
                        combined.push(cp);
                    }
                });

                combined.sort((a, b) => {
                    const pinnedIds = viewedUser.data.pinnedPosts || [];
                    const aPinned = pinnedIds.includes(a.id) && a.author === viewedUser.id;
                    const bPinned = pinnedIds.includes(b.id) && b.author === viewedUser.id;
                    if (aPinned && !bPinned) return -1;
                    if (!aPinned && bPinned) return 1;
                    if (aPinned && bPinned) return pinnedIds.indexOf(a.id) - pinnedIds.indexOf(b.id);
                    return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
                });

                return combined;
            }
            case 'Faves':
                return isOwner ? unarchivedAllPosts.filter(p => viewedUser.data.likedPosts?.includes(p.id)) : [];
            case 'Saves':
                return isOwner ? unarchivedAllPosts.filter(p => viewedUser.data.bookmarkedPosts?.includes(p.id)) : [];
            case 'All posts':
            default:
                return pinSortedUserPosts;
        }
    }, [activeTab, posts, userPosts, allPosts, isOwner, viewedUser.data]);

    const handleToggleSelectAll = () => {
        const filteredIds = new Set(filteredPosts.map(p => p.id));
        const allFilteredSelected = filteredPosts.length > 0 && filteredPosts.every(p => selectedPostIds.has(p.id));

        setSelectedPostIds(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredPosts.forEach(p => next.delete(p.id));
            } else {
                filteredPosts.forEach(p => next.add(p.id));
            }
            return next;
        });
    };

    const isAllSelected = filteredPosts.length > 0 && filteredPosts.every(p => selectedPostIds.has(p.id));

    const headerTextColor = viewedUser.data.appTextColor || '#ffffff';
    const headerFontFamily = viewedUser.data.appFontFamily || undefined;

    return (
        <div className="relative">
            {viewedUser.data.passion && showPassionOverlay && (
                <div className="fixed inset-0 z-[9999] flex flex-col bg-[#0d1117] text-white">
                    <div className="flex items-center justify-between px-4 pb-3 pt-[calc(0.75rem+var(--safe-area-top,0px))] border-b border-white/10 bg-[#161b22]" style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowPassionOverlay(false)}
                                className="relative w-10 h-10 rounded-full border border-white/20 active:scale-95 transition-all overflow-hidden flex-shrink-0"
                                title="Back to profile"
                            >
                                <img src={viewedUser.data.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(viewedUser.id) + '&backgroundColor=b6e3f4'} alt="Back" className="w-full h-full object-cover" />
                            </button>
                            <div>
                                <div className="text-xs font-bold truncate max-w-[180px]">{viewedUser.data.displayName}</div>
                                <div className="text-[10px] text-gray-400 truncate max-w-[180px]">{viewedUser.data.passion}</div>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowPassionOverlay(false)}
                            className="px-3 py-1 text-xs font-semibold bg-white/10 rounded-lg active:scale-95 transition-all hover:bg-white/20"
                        >
                            Done
                        </button>
                    </div>

                    <div className="flex-1 w-full overflow-hidden bg-black">
                        <ProxiedIframe
                            url={viewedUser.data.passion}
                            className="w-full h-full border-none"
                            style={{ height: '100%', width: '100%' }}
                        />
                    </div>
                </div>
            )}

            <ProfileHeader
                userData={viewedUser.data}
                userId={viewedUser.id}
                isOwner={isOwner}
                onProfilePictureChange={onProfilePictureChange}
                onProfileBgChange={onProfileBgChange}
                onStartConversation={onStartConversation}
                postCount={userPosts.length}
                onFollowToggle={onFollowToggle}
                onGrantAdmin={onGrantAdmin}
                onDeleteUser={onDeleteUser}
                onMuteUser={onMuteUser}
                onBlockUser={onBlockUser}
                onRemoveFollower={onRemoveFollower}
                onShowFollowers={() => onShowFollowList('friends', viewedUser.id)}
                onShowFollowing={() => onShowFollowList('following', viewedUser.id)}
                onShowConnections={() => onShowFollowList('connections', viewedUser.id)}
                onShare={onShare}
                canSeeComments={canSeeComments}
                onInternalModalStateChange={setHeaderModalOpen}
                closeInternalModalsTrigger={closeInternalModalsTrigger}
                onNavigateToYipYap={onNavigateToYipYap}
                existingNonAnonymousYipYapNames={existingNonAnonymousYipYapNames}
                onNavigateToProfile={onNavigateToProfile}
                onNavigateToHashtag={onNavigateToHashtag}
                showPassionOverlay={showPassionOverlay}
                onPfpClick={() => viewedUser.data.passion && setShowPassionOverlay(!showPassionOverlay)}
            />

            {!showPassionOverlay && (
                <div className={`transition-opacity duration-500 ${hideContent ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                    {isReorderMode && (
                        <div 
                            className="fixed inset-0 z-40 bg-transparent" 
                            onClick={() => setIsReorderMode(false)}
                            onTouchStart={() => setIsReorderMode(false)}
                        />
                    )}
                    <div className={`w-full px-4 lg:px-[5%] mb-4 ${isReorderMode ? 'relative z-50' : ''}`}>
                        <nav
                            className="flex space-x-6 overflow-x-auto no-scrollbar py-1"
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{ touchAction: isReorderMode ? 'none' : 'pan-x', fontFamily: headerFontFamily }}
                        >
                            <Reorder.Group 
                                axis="x" 
                                values={profileTabs} 
                                onReorder={(newOrder) => onReorderTabs && onReorderTabs(newOrder)} 
                                className="flex space-x-6 h-full"
                                style={{ overflow: 'visible' }}
                            >
                                {profileTabs.map(tab => (
                                    <DraggableTab
                                        key={tab}
                                        item={tab}
                                        label={tab}
                                        isActive={activeTab === tab}
                                        onClick={() => setActiveTab(tab)}
                                        color={headerTextColor}
                                        isReorderMode={isReorderMode}
                                        onEnterReorderMode={() => setIsReorderMode(true)}
                                        onExitReorderMode={() => setIsReorderMode(false)}
                                    />
                                ))}
                            </Reorder.Group>
                        </nav>
                    </div>
                    <PostGrid key={activeTab} posts={filteredPosts} onPostClick={(p) => onPostClick(p, filteredPosts)} onLike={onLike} onBump={onBump} onBookmark={onBookmark} onEdit={onEdit} onDelete={onDelete} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} onNavigateToCommunity={onNavigateToCommunity} onShare={onShare} onVouch={onVouch} onMuteUser={onMuteUser} onBlockUser={onBlockUser} onGrantAdmin={onGrantAdmin} onDeleteUser={onDeleteUser} onReportPost={onReportPost} animatingLikes={animatingLikes} animatingBumps={animatingBumps} animatingBookmarks={animatingBookmarks} galleryAnimation={galleryAnimation} postBlurOpacity={postBlurOpacity} preferredColumns={gridColumns} onColumnsChange={onGridColumnsChange} preferredScale={preferredScale} onScaleChange={onScaleChange}
                        onSwipeTransition={onSwipeTransition}
                        isModalOpen={isModalOpen}
                        initialVisibleCount={initialVisibleCount}
                        onVisibleCountChange={onVisibleCountChange}
                        isLoading={isLoading}
                        onPin={onPin}
                        onUnpin={onUnpin}
                        onReorderPinnedPosts={onReorderPinnedPosts}
                        isProfile={true}
                        onRetryPostUpload={onRetryPostUpload}
                        onAddToCart={onAddToCart}
                        postcardGap={postcardGap}
                        onPostDragProgress={onPostDragProgress}
                        massEditMode={massEditMode}
                        selectedPostIds={selectedPostIds}
                        onToggleSelectPost={toggleSelectPost}
                    />
                </div>
            )}

            {/* Mass Edit Floating Bar */}
            {massEditMode && (
                <div className="fixed inset-x-0 bottom-0 flex flex-col items-center justify-end z-[180] px-6 py-2 pb-[calc(5rem+var(--safe-area-bottom,0px))] pointer-events-none">
                    <div
                        className="pointer-events-auto w-[380px] max-w-[calc(100vw-2rem)] rounded-[2.5rem] px-5 py-3.5 flex items-center justify-between gap-3 shadow-2xl border border-white/20 backdrop-blur-xl animate-fade-in"
                        style={{ backgroundColor: modalBg, color: userTextColor }}
                    >
                        <div className="flex items-center gap-2.5">
                            <button
                                onClick={() => {
                                    setMassEditMode(null);
                                    setSelectedPostIds(new Set());
                                }}
                                className="w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 border border-white/20 flex items-center justify-center text-white transition-all active:scale-95 flex-shrink-0"
                                title="Cancel"
                            >
                                ✕
                            </button>
                            <button
                                onClick={handleToggleSelectAll}
                                className="px-3.5 py-2 rounded-2xl text-xs font-bold bg-black/30 hover:bg-black/50 border border-white/10 transition-all active:scale-95 flex items-center gap-2 flex-shrink-0"
                                style={{ color: userTextColor, ...stickerTextStyle(0.18) }}
                            >
                                <span
                                    className={`w-4 h-4 rounded-full border flex items-center justify-center text-[10px] ${
                                        isAllSelected ? 'text-white' : 'border-white/50 text-transparent'
                                    }`}
                                    style={isAllSelected ? { backgroundColor: userThemeColor, borderColor: userThemeColor } : {}}
                                >
                                    ✓
                                </span>
                                {isAllSelected ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                        <span className="text-xs font-black tracking-wider uppercase opacity-90 px-1 flex-shrink-0" style={stickerTextStyle(0.18)}>
                            {selectedPostIds.size}/{filteredPosts.length}
                        </span>
                        <button
                            onClick={handleActionDone}
                            disabled={selectedPostIds.size === 0}
                            className={`flex-1 max-w-[120px] py-2.5 px-4 rounded-2xl text-xs font-black uppercase tracking-wider transition-all active:scale-95 shadow-lg ${
                                selectedPostIds.size === 0
                                    ? 'opacity-40 cursor-not-allowed bg-black/40 text-white/50'
                                    : massEditMode === 'delete'
                                    ? 'bg-red-600 hover:bg-red-500 text-white'
                                    : 'text-white'
                            }`}
                            style={
                                selectedPostIds.size > 0 && massEditMode !== 'delete'
                                    ? { backgroundColor: userThemeColor, ...stickerTextStyle(0.18) }
                                    : stickerTextStyle(0.18)
                            }
                        >
                            {massEditMode === 'edit' ? 'Edit' : massEditMode === 'archive' ? 'Archive' : 'Delete'}
                        </button>
                    </div>
                </div>
            )}

            {/* Mass Edit Settings Customize Modal */}
            <PostOptionsModal
                isOpen={isMassEditCustomizeModalOpen}
                onClose={() => setIsMassEditCustomizeModalOpen(false)}
                title="Mass Edit Posts"
                onConfirm={(data) => {
                    setPendingMassUpdateData(data);
                    setIsMassEditCustomizeModalOpen(false);
                    setShowMassConfirmModal(true);
                }}
            />

            {/* Mass Action Confirmation Modal */}
            {showMassConfirmModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[250] p-4 animate-fade-in">
                    <div className="rounded-[2.5rem] p-8 max-w-md w-full mx-4 text-center backdrop-blur-md border border-white/10 bg-black/70 text-white flex flex-col items-center">
                        <h2 className="text-2xl font-black italic uppercase tracking-tighter mb-4 text-amber-400" style={stickerTextStyle(0.15)}>
                            Confirm Mass {massEditMode === 'edit' ? 'Edit' : massEditMode === 'archive' ? 'Archive' : 'Delete'}
                        </h2>
                        <p className="mb-6 opacity-90 text-sm leading-relaxed" style={stickerTextStyle(0.18)}>
                            Are you sure you want to {massEditMode === 'edit' ? 'update options for' : massEditMode === 'archive' ? 'archive' : 'permanently delete'} <b>{selectedPostIds.size}</b> selected post{selectedPostIds.size === 1 ? '' : 's'}?
                            {massEditMode === 'delete' && ' This action cannot be undone.'}
                        </p>
                        <div className="flex justify-center gap-4 w-full">
                            <button
                                onClick={() => setShowMassConfirmModal(false)}
                                className="flex-1 font-bold py-2.5 px-6 rounded-full transition-all bg-white/10 hover:bg-white/20 text-white"
                                style={stickerTextStyle(0.18)}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    const ids = Array.from(selectedPostIds);
                                    if (massEditMode === 'edit' && pendingMassUpdateData && onMassEdit) {
                                        onMassEdit(ids, pendingMassUpdateData);
                                    } else if (massEditMode === 'archive' && onMassArchive) {
                                        onMassArchive(ids);
                                    } else if (massEditMode === 'delete' && onMassDelete) {
                                        onMassDelete(ids);
                                    }
                                    setShowMassConfirmModal(false);
                                    setMassEditMode(null);
                                    setSelectedPostIds(new Set());
                                    setPendingMassUpdateData(null);
                                }}
                                className={`flex-1 font-bold py-2.5 px-6 rounded-full transition-all shadow-lg active:scale-95 text-white ${
                                    massEditMode === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                                style={stickerTextStyle(0.18)}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
};
